import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Check, ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/site";
import CTABand from "@/components/CTABand";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`page-service-${slug}`}>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-teh-deep">
          <img src={service.image} alt={service.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-teh-deep/90 via-teh-navy/70 to-teh-navy/30" />
        </div>

        <div className="relative z-10 teh-container">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            All services
          </Link>
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/15 backdrop-blur text-white">
                <Icon className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-300">
                1TEH Division
              </p>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              {service.name}
            </h1>
            <p className="mt-6 font-serif text-2xl md:text-3xl text-emerald-300 italic">
              {service.short}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-teh-bg">
        <div className="teh-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="teh-overline">Overview</p>
            <p className="mt-6 text-xl text-teh-deep leading-relaxed font-serif">
              {service.description}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {service.external_url ? (
                <a
                  href={service.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-detail-external"
                  className="inline-flex items-center gap-2 px-7 py-4 bg-teh-green text-white text-sm font-medium rounded-full hover:bg-teh-greenDark hover:gap-3 transition-all"
                >
                  Learn more at {service.external_url.replace(/^https?:\/\//, "")}
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : service.action_url ? (
                <Link
                  to={service.action_url}
                  data-testid="service-detail-action"
                  className="inline-flex items-center gap-2 px-7 py-4 bg-teh-green text-white text-sm font-medium rounded-full hover:bg-teh-greenDark hover:gap-3 transition-all"
                >
                  {service.action_label || "Learn more"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : null}
              <Link
                to="/contact"
                data-testid="service-detail-cta"
                className="inline-flex items-center gap-2 px-7 py-4 bg-teh-navy text-white text-sm font-medium rounded-full hover:bg-teh-navyDark hover:gap-3 transition-all"
              >
                Request a referral
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-teh-navy/10 rounded-2xl p-8">
              <p className="teh-overline">What's Included</p>
              <ul className="mt-6 space-y-4">
                {service.capabilities.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-teh-green/10 flex items-center justify-center mt-0.5 shrink-0">
                      <Check className="w-3 h-3 text-teh-green" strokeWidth={2.5} />
                    </div>
                    <span className="text-teh-deep">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-teh-surfaceAlt">
        <div className="teh-container">
          <p className="teh-overline">More Divisions</p>
          <h2 className="font-serif text-3xl md:text-4xl mt-3 text-teh-deep mb-12">
            Explore the rest of the enterprise
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {others.map((s) => {
              const SIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group p-7 bg-white border border-teh-navy/10 rounded-2xl hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <SIcon className="w-8 h-8 text-teh-green mb-5" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-teh-deep">{s.name}</h3>
                  <p className="mt-2 text-sm text-teh-muted">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
